import React, { useEffect, useState, useCallback } from 'react';
import {
  FormControl,
  useMediaQuery,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import {
  Autocomplete,
  Checkbox,
  DatePicker,
  HebrewDatePicker,
  Input,
  Select,
  gregorianToHebrewString,
  type SelectOption
} from '@sm-dashboard/ui-components';
import { CustomSlider } from '@sm-dashboard/form-provider';
import { FilterConfig, FilterOptionType } from '@sm-dashboard/types';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { SearchOutlined } from '@mui/icons-material';
import { FilterPanelInput } from './FilterPanelInput';
import { leadsSourceOptions, sourceOptions, statusArr } from './filterOptions';
import { isSearchFilter } from './filterFieldUtils';

export { leadsSourceOptions, sourceOptions, statusArr };

const flattenOptions = (options: FilterOptionType[], parentValue?: string): SelectOption[] => {
  const result: SelectOption[] = [];
  for (const option of options) {
    const segment = String(option.value);
    const fullValue = parentValue ? `${parentValue}-${segment}` : segment;
    if (option.options?.length) {
      result.push(...flattenOptions(option.options, segment));
    } else {
      result.push({ label: option.label || option.name || segment, value: fullValue });
    }
  }
  return result;
};

const toMultiSelectOptions = (options: FilterOptionType[]): SelectOption[] =>
  options.map(opt => ({
    value: String(opt.value ?? opt.label),
    label: opt.label || opt.name || String(opt.value),
    disabled: opt.disabled
  }));

const handleFilterChange = (
  filter: FilterConfig<any>,
  value: any,
  handleApplyFilters: (filter: FilterConfig<any>, value: any) => void
) => {
  if (filter.type === 'multiSelect') {
    value = convertUniqueValues(value);
  }
  handleApplyFilters(filter, value);
};

const convertUniqueValues = (arr: (string | number)[]) => {
  const stringArr = arr.map(item => item.toString());
  const doubleValues = stringArr.filter((item: string, index: number) => stringArr.indexOf(item) !== index);
  const filteredValues = stringArr.filter((item: string, index: number) => doubleValues.indexOf(item) === -1);
  return filteredValues;
};

// Custom debounce hook
const useDebounce = (callback: (...args: any[]) => void, delay: number) => {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: any[]) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);

      setTimeoutId(newTimeoutId);
    },
    [callback, delay, timeoutId]
  );

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
};

interface FilterInputProps<T> {
  filter: FilterConfig<T>;
  handleApplyFilters: (filter: FilterConfig<any>, value: any) => void;
  isPrimary?: boolean;
}

export const FilterInput = <T,>({ filter, handleApplyFilters, isPrimary }: FilterInputProps<T>) => {
  const [searchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState<FilterOptionType | null>(null);
  const [choosedValue, setChoosedValue] = useState<FilterOptionType | null>(() => {
    const storedValue = localStorage.getItem('choosedFilterValue');
    if (storedValue !== 'undefined' && storedValue !== null) {
      const parsedValue = JSON.parse(storedValue);
      return Object.keys(parsedValue).length > 0 && searchParams.get(filter.field as string) === parsedValue.value
        ? parsedValue
        : null;
    }
    return null;
  });

  // Internal state for text and sliderRange filters
  const [textValue, setTextValue] = useState<string>(() => {
    return searchParams.get(filter.field as string) || filter.defaultValue || '';
  });

  // Create debounced version of handleFilterChange for text and sliderRange filters
  const debouncedHandleFilterChange = useDebounce(
    (value: any) => handleFilterChange(filter, value, handleApplyFilters),
    1000 // 1000ms delay
  );

  // Sync internal state with URL params when they change
  useEffect(() => {
    const urlValue = searchParams.get(filter.field as string);
    if (filter.type === 'text' || filter.type === 'number' || filter.type === undefined) {
      setTextValue(urlValue || filter.defaultValue || '');
    }
  }, [searchParams, filter.field, filter.type, filter.defaultValue]);

  useEffect(() => {
    if (choosedValue) {
      localStorage.setItem('choosedFilterValue', JSON.stringify(choosedValue));
    }
  }, [choosedValue]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const forHandleFilterChange = (value: any) => {
    // Use debounced version for text and sliderRange filters
    if (filter.type === 'text' || filter.type === 'number' || filter.type === undefined) {
      debouncedHandleFilterChange(value);
    } else {
      // Immediate execution for other filter types
      handleFilterChange(filter, value, handleApplyFilters);
    }
  };

  // Handle text input changes with immediate state update
  const handleTextChange = (value: string) => {
    setTextValue(value);
    debouncedHandleFilterChange(value);
  };

  if (!isPrimary) {
    return <FilterPanelInput filter={filter} handleApplyFilters={handleApplyFilters} />;
  }

  if ((filter.type === 'select' || filter.type === 'multiSelect') && !filter.options) {
    console.error('options are required for select filter');
    return null;
  }

  const sortOptions = (options: FilterOptionType[]) =>
    [...options].sort((a, b) => {
      if ((!a.options || a.options.length === 0) && b.options && b.options.length > 0) return -1;
      if (a.options && a.options.length > 0 && (!b.options || b.options.length === 0)) return 1;
      return 0;
    });

  switch (filter.type) {
    case 'date':
      return (
        <Box sx={{ minWidth: 100 }}>
          <DatePicker
            value={(searchParams.get(filter.field as string) || filter.defaultValue) || ''}
            onChange={value => forHandleFilterChange(value)}
            label={filter.label}
          />
        </Box>
      );
    case 'hebrewDate': {
      const gregorian = searchParams.get(filter.field as string) || filter.defaultValue || '';
      return (
        <Box sx={{ minWidth: 180, overflow: 'visible' }}>
          <HebrewDatePicker
            appearance="plain"
            label={filter.label}
            placeholder={filter.label}
            hebrewDate={gregorianToHebrewString(gregorian) || ''}
            gregorianDate={gregorian}
            onHebrewDateChange={next => {
              if (!next) forHandleFilterChange(null);
            }}
            onGregorianDateChange={date => forHandleFilterChange(date || null)}
          />
        </Box>
      );
    }
    case 'select':
    case 'multiSelect':
    case 'status':
    case 'sourceSelect': {
      const options = filter.type === 'sourceSelect' ? (filter.options ?? sourceOptions) : filter.options || statusArr;
      const sortedOptions = sortOptions(options);
      const fieldSx = { minWidth: 100, width: !isSmallScreen ? filter.width : '100%' };

      if (filter.type === 'multiSelect') {
        let value: string[] = searchParams.get(filter.field as string) || filter.defaultValue || '';
        if (!Array.isArray(value)) {
          value = value ? String(value).split(',') : [];
        }

        return (
          <Box sx={fieldSx}>
            <Select
              appearance="plain"
              label={filter.label}
              placeholder={filter.label}
              multiple
              value={value}
              onChange={forHandleFilterChange}
              options={toMultiSelectOptions(sortedOptions)}
              fullWidth
            />
          </Box>
        );
      }

      const raw = searchParams.get(filter.field as string) || filter.defaultValue || '';

      return (
        <Box sx={fieldSx}>
          <Select
            appearance="plain"
            placeholder={filter.label}
            value={raw ? String(raw) : undefined}
            onChange={v => forHandleFilterChange(v || null)}
            options={flattenOptions(sortedOptions)}
            fullWidth
          />
        </Box>
      );
    }
    case 'autocomplete': {
      const paramValue = searchParams.get(filter.field as string) || filter.defaultValue;
      const selectedOption = paramValue
        ? (filter.options || []).find(option => {
          if (typeof option.value === 'string' && option.value.includes('-')) {
            return option.value === paramValue || option.value.split('-')[1] === String(paramValue);
          }
          return option.value === paramValue || String(option.value) === String(paramValue);
        })
        : null;

      return (
        <FormControl
          fullWidth={!filter.width || isSmallScreen}
          size="small"
          sx={{ minWidth: 100, width: !isSmallScreen ? filter.width : '100%' }}
        >
          <Autocomplete
            blurOnSelect
            options={filter.options || []}
            appearance='plain'
            value={selectedOption}
            onChange={(_, value) => forHandleFilterChange(value)}
            getOptionLabel={option => option?.label || option?.name || ''}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;

              if (typeof option === 'object' && typeof value === 'object') {
                return option.value === value.value;
              }

              if (typeof value === 'object' && value.value !== undefined) {
                return option.value === value.value;
              }

              if (typeof option === 'object' && option.value !== undefined) {
                if (typeof option.value === 'string' && option.value.includes('-')) {
                  return option.value === String(value) || option.value.split('-')[1] === String(value);
                }
                return option.value === String(value) || String(option.value) === String(value);
              }

              return option === value;
            }}
            label={filter.label}
          />
        </FormControl>
      );
    }
    case 'custom':
      return (
        <Box sx={{ width: !isSmallScreen ? filter.width : '100%' }}>
          {filter.render?.({
            value: searchParams.get(filter.field as string),
            onChange: forHandleFilterChange,
            placeholder: filter.label
          })}
        </Box>
      );

    case 'dialog':
      return (
        <>
          <Button sx={{ width: '100%' }} variant="outlined" onClick={() => setDialogOpen(true)}>
            {filter.label}
            {choosedValue ? ': ' + (choosedValue?.label || choosedValue?.name) : ''}
          </Button>
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogTitle>{filter.label}</DialogTitle>
            <DialogContent>
              {filter.dialog && React.createElement(filter.dialog, { setState: setDialogValue })}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setDialogValue(null);
                  setDialogOpen(false);
                }}
              >
                ביטול
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setChoosedValue(dialogValue);
                  forHandleFilterChange(dialogValue?.value);
                  setDialogOpen(false);
                }}
              >
                אישור
              </Button>
            </DialogActions>
          </Dialog>
        </>
      );

    case 'sliderRange':
      // In mobile, show two separate number inputs instead of slider
      if (isSmallScreen) {
        const currentValue = searchParams.get(filter.field as string)?.split(',') || filter.defaultValue || [0, 10000];
        const [minValue, maxValue] = currentValue;

        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
            <Input
              appearance="plain"
              type="number"
              placeholder="מסכום"
              value={minValue}
              onChange={e => {
                const newValue = [e.target.value, maxValue];
                forHandleFilterChange(newValue);
              }}
              inputProps={{ min: filter.min, max: filter.max }}
            />
            <Input
              appearance="plain"
              type="number"
              placeholder="עד סכום"
              value={maxValue}
              onChange={e => {
                const newValue = [minValue, e.target.value];
                forHandleFilterChange(newValue);
              }}
              inputProps={{ min: filter.min, max: filter.max }}
            />
          </Box>
        );
      }

      // Desktop: show slider as before
      return (
        <CustomSlider
          onChange={value => forHandleFilterChange(value)}
          defaultValue={searchParams.get(filter.field as string)?.split(',') || filter.defaultValue || []}
          label={filter.label}
          min={filter.min}
          max={filter.max}
          width={isSmallScreen ? '100%' : filter.width}
          labelPosition="left"
        />
      );

    case 'checkbox':
      return (
        <Checkbox
          size="small"
          checked={searchParams.get(filter.field as string) === 'true'}
          onChange={(_, checked) => forHandleFilterChange(checked)}
          label={<span style={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{filter.label}</span>}
        />
      );
    case 'number':
    case 'text':
    default:
      return (
        <Input
          appearance={isSearchFilter(filter) ? 'default' : 'plain'}
          placeholder={filter.label}
          type={filter.type === 'number' ? 'number' : 'text'}
          value={textValue}
          onChange={e => handleTextChange(e.target.value)}
          endIcon={
            isSearchFilter(filter) ? (
              <SearchOutlined sx={{ color: 'text.secondary', fontSize: '18px' }} />
            ) : undefined
          }
        />
      );
  }
};
